import { NameAndCode } from '@/fetching/sharedSchemas/basicSchemas';
import { OrderPartialMap } from '@/types/lists/ordering';
import {
    Ordering,
    QueryVisitsArgs,
    VisitDatetimeField,
    VisitFilter,
    VisitOutcome,
    VisitPriority,
    VisitState,
} from 'gql-types';

import { EpisodeOfCare } from './episodeOfCare';
import { OptionValue } from './optionValue';
import { Organization } from './organization';
import { Patient } from './patient';

export { VisitOutcome, VisitPriority, VisitState } from 'gql-types';

/*****************
 * Visit Lists
 *****************/
export namespace Visit {
    export type Filters = VisitFilter;
    export type Ordering = VisitOrdering;
    export type ListVariables = QueryVisitsArgs;
}

export { VisitDatetimeField } from './graphql';

/**
 * A time constraint for comparing datetime fields between related visits.
 * All specified offsets must be satisfied (AND logic).
 */
export type RelativeTimeConstraintInput = {
    /** The datetime field on the related (inner) visit to compare */
    field: VisitDatetimeField;
    /** The datetime field on the outer visit to compare against */
    relativeTo: VisitDatetimeField;
    /** related.field > (outer.relativeTo + offset) */
    gtOffsetMinutes?: number;
    /** related.field >= (outer.relativeTo + offset) */
    gteOffsetMinutes?: number;
    /** related.field < (outer.relativeTo + offset) */
    ltOffsetMinutes?: number;
    /** related.field <= (outer.relativeTo + offset) */
    lteOffsetMinutes?: number;
};

/**
 * Condition for finding related visits to the visit being filtered.
 *
 * This enables filtering visits based on the existence (or non-existence) of
 * other visits that share characteristics with the outer visit and match
 * additional criteria.
 *
 * Example - find visits that need an SFV follow-up (have metadata sfv_required
 * but no confirmed/scheduled SFV visit within 48 hours):
 * {
 *   exists: false,
 *   filter: {
 *     services: { externalIds: { value: { exact: "SFV_SERVICE_ID" } } },
 *     state: { inList: [CONFIRMED, SCHEDULED] }
 *   },
 *   timeConstraints: [{
 *     field: START_DATETIME,
 *     relativeTo: START_DATETIME,
 *     gteOffsetMinutes: 0,
 *     lteOffsetMinutes: 2880  // 48 hours
 *   }],
 *   samePatient: true,
 *   sameEpisode: true
 * }
 */
export type RelatedVisitConditionInput = {
    /** Whether a matching related visit must exist (true) or must NOT exist (false) */
    exists: boolean;
    /** Filter to apply to candidate related visits */
    filter?: Visit.Filters;
    /** Time constraints relative to the outer visit (all must be satisfied) */
    timeConstraints?: RelativeTimeConstraintInput[];
    /** Whether related visits must have the same patient (default: true) */
    samePatient?: boolean;
    /** Whether related visits must have the same episode (default: true) */
    sameEpisode?: boolean;
};

type VisitOrdering = OrderPartialMap<{
    organization: Organization.Ordering;
    patient: Patient.Ordering;
    episode: EpisodeOfCare.Ordering;
    startDatetime: Ordering;
    endDatetime: Ordering;
    effectiveDate: Ordering;
    tentativeDate: Ordering;
    cancellationReason: OptionValue.Ordering;
    createdAt: Ordering;
    updatedAt: Ordering;
    earliestStartDatetime: Ordering;
    latestEndDatetime: Ordering;
    searchRanking: Ordering;
}>;

export const VisitPriorityOrder = {
    [VisitPriority.STANDARD]: 0,
    [VisitPriority.HIGH]: 1,
    [VisitPriority.URGENT]: 2,
};

export const renderVisitPriority = (priority: VisitPriority): string => {
    switch (priority) {
        case VisitPriority.HIGH:
            return 'High';
        case VisitPriority.STANDARD:
            return 'Standard';
        case VisitPriority.URGENT:
            return 'Urgent';
        default:
            return 'Unknown';
    }
};

// Temp: conversion between graphql and zod types
export const visitPriorityToZod = (priority: VisitPriority): NameAndCode => {
    return {
        code: priority,
        name: renderVisitPriority(priority),
    };
};

export const isUrgent = (priority: VisitPriority): boolean => priority === VisitPriority.HIGH;

export const COMPLETE_VISIT_STATES = [VisitState.ENDED, VisitState.IN_REVIEW];
export const VISIT_STATES_THAT_REQUIRE_OUTCOME = [VisitState.ENDED, VisitState.IN_REVIEW];

export const visitStateRequiresOutcome = (state: VisitState): boolean => {
    return VISIT_STATES_THAT_REQUIRE_OUTCOME.includes(state);
};

export const isVisitComplete = (state: VisitState): boolean => {
    return COMPLETE_VISIT_STATES.includes(state);
};

export const renderVisitState = (state: VisitState): string => {
    switch (state) {
        case VisitState.UNSCHEDULED:
            return 'Unscheduled';
        case VisitState.SCHEDULED:
            return 'Scheduled';
        case VisitState.CONFIRMED:
            return 'Confirmed';
        case VisitState.EN_ROUTE:
            return 'En Route';
        case VisitState.AT_LOCATION:
            return 'At Location';
        case VisitState.IN_REVIEW:
            return 'In Review';
        case VisitState.ENDED:
            return 'Ended';
        default:
            return 'Unknown';
    }
};
export const visitStateToZod = (state: VisitState): NameAndCode => {
    return {
        code: state,
        name: renderVisitState(state),
    };
};

export const ReschedulableOutcomes = new Set<VisitOutcome>([
    VisitOutcome.MISSED,
    VisitOutcome.PATIENT_DID_NOT_SHOW,
]);

export const isOutcomeReschedulable = (outcome: VisitOutcome): boolean => {
    return ReschedulableOutcomes.has(outcome);
};

const visitOutcomeToZodCode = (outcome: VisitOutcome): string => {
    switch (outcome) {
        case VisitOutcome.PATIENT_CANCELED:
            return 'CANCELED';
        case VisitOutcome.PATIENT_LATE_CANCELED:
            return 'LATE_CANCELED';
        case VisitOutcome.PATIENT_DID_NOT_SHOW:
            return 'DID_NOT_SHOW';
        default:
            return outcome;
    }
};

export const renderVisitOutcome = (outcome: VisitOutcome): string => {
    switch (outcome) {
        case VisitOutcome.NONE:
            return 'None';
        case VisitOutcome.COMPLETED:
            return 'Completed';
        case VisitOutcome.MISSED:
            return 'Missed';
        case VisitOutcome.PATIENT_CANCELED:
            return 'Canceled';
        case VisitOutcome.PATIENT_LATE_CANCELED:
            return 'Late Canceled';
        case VisitOutcome.PATIENT_DID_NOT_SHOW:
            return 'Patient Did Not Show';
        case VisitOutcome.UNSUCCESSFUL:
            return 'Unsuccessful';
        case VisitOutcome.UNAVAILABLE:
            return 'Unavailable';
        case VisitOutcome.OTHER:
            return 'Other';
        default:
            return 'Unknown';
    }
};

export const visitOutcomeToZod = (outcome: VisitOutcome): NameAndCode => {
    return {
        code: visitOutcomeToZodCode(outcome),
        name: renderVisitOutcome(outcome),
    };
};

/*** Colors ***/
export enum VisitStateAndOutcomeColors {
    GRAY = '#9E9E9E',
    RED = '#F44336',
    ORANGE = '#FDB13E',
    BLUE = '#62BFC1',
}

export const VisitPriorityColors: Record<VisitPriority, string> = {
    [VisitPriority.STANDARD]: '#e7f5f4',
    [VisitPriority.HIGH]: '#b6e0df',
    [VisitPriority.URGENT]: '#83cbc9',
};

export const VisitStateColors: Record<VisitState, string> = {
    [VisitState.UNSCHEDULED]: VisitStateAndOutcomeColors.GRAY,
    [VisitState.SCHEDULED]: VisitStateAndOutcomeColors.GRAY,
    [VisitState.CONFIRMED]: VisitStateAndOutcomeColors.GRAY,
    [VisitState.EN_ROUTE]: VisitStateAndOutcomeColors.ORANGE,
    [VisitState.AT_LOCATION]: VisitStateAndOutcomeColors.ORANGE,
    [VisitState.IN_REVIEW]: VisitStateAndOutcomeColors.ORANGE,
    [VisitState.ENDED]: VisitStateAndOutcomeColors.BLUE,
};

export const VisitOutcomeColors: Record<VisitOutcome, string> = {
    [VisitOutcome.NONE]: VisitStateAndOutcomeColors.GRAY,
    [VisitOutcome.COMPLETED]: VisitStateAndOutcomeColors.BLUE,
    [VisitOutcome.MISSED]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.PATIENT_CANCELED]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.PATIENT_LATE_CANCELED]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.PATIENT_DID_NOT_SHOW]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.UNSUCCESSFUL]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.UNAVAILABLE]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.OTHER]: VisitStateAndOutcomeColors.RED,
};
